import React, { useEffect, useMemo, useState } from "react";
import {
  FaCheckCircle,
  FaEnvelope,
  FaExclamationTriangle,
  FaFacebookF,
  FaInstagram,
  FaLocationArrow,
  FaMapMarkerAlt,
  FaPaperPlane,
  FaPhoneAlt,
  FaSpinner,
} from "react-icons/fa";

import Reveal from "../../../components/Reveal/Reveal";
import { saveMessageContactForm } from "../../../services/contactformServices";
import { sendNotificationToAdmin } from "../../../services/notificationServices";
import { getSettings } from "../../../services/settingsServices";

const INITIAL_FORM = {
  name: "",
  email: "",
  message: "",
  website: "",
};

const isExternalURL = (url) => {
  try {
    const newUrl = new URL(url);
    return newUrl.protocol === "http:" || newUrl.protocol === "https:";
  } catch (error) {
    return false;
  }
};

const normalizePhoneHref = (phone = "") => {
  const clean = String(phone).replace(/\s+/g, "");
  return clean ? `tel:${clean}` : "";
};

const inputBase =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3.5 text-sm font-medium text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-primary focus:ring-4 focus:ring-orange-500/10 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:placeholder:text-slate-500 dark:focus:border-primary";

const labelBase =
  "mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200";

const ContactInfoItem = ({ icon: Icon, label, value, href }) => {
  if (!value) return null;

  const content = (
    <div className="group flex items-start gap-4 rounded-[1.5rem] border border-white/10 bg-white/10 p-4 backdrop-blur-xl transition hover:bg-white/15">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-primary shadow-lg">
        <Icon />
      </div>

      <div className="min-w-0">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/50">
          {label}
        </p>
        <p className="mt-1 break-words text-sm font-semibold leading-6 text-white">
          {value}
        </p>
      </div>
    </div>
  );

  if (!href) return content;

  return (
    <a href={href} className="block">
      {content}
    </a>
  );
};

export default function FormContact() {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [clubInfo, setClubInfo] = useState({
    nom: "Le club",
    email: "",
    phone: "",
    address: "",
    facebook: "",
    instagram: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [notificationSent, setNotificationSent] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      try {
        const response = await getSettings();
        const settings = Array.isArray(response) ? response[0] : response;

        if (!mounted || !settings) return;

        setClubInfo({
          nom: settings.nom_club || "Le club",
          email: settings.email_club || settings.email || "",
          phone: settings.telephone_club || settings.phone || "",
          address:
            settings.adresse_club ||
            settings.address ||
            [settings.adresse, settings.code_postal, settings.ville]
              .filter(Boolean)
              .join(" "),
          facebook: settings.facebook_club || "",
          instagram: settings.instagram_club || "",
        });
      } catch (error) {
        console.error("Erreur lors du chargement des informations du club :", error);
      }
    };

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  const phoneHref = useMemo(
    () => normalizePhoneHref(clubInfo.phone),
    [clubInfo.phone]
  );

  const emailHref = useMemo(() => {
    return clubInfo.email ? `mailto:${clubInfo.email}` : "";
  }, [clubInfo.email]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));

    if (notificationSent) setNotificationSent(false);
    if (errorMsg) setErrorMsg("");
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      return "Merci d’indiquer votre nom et prénom.";
    }

    if (!formData.email.trim()) {
      return "Merci d’indiquer votre adresse email.";
    }

    if (!formData.message.trim()) {
      return "Merci d’écrire votre message.";
    }

    return "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (formData.website) {
      return;
    }

    const validationError = validateForm();

    if (validationError) {
      setErrorMsg(validationError);
      return;
    }

    const name = formData.name.trim();
    const email = formData.email.trim();
    const message = formData.message.trim();

    const notificationMessage = `Vous avez reçu un message du formulaire de contact de ${name} (${email})`;

    try {
      setSubmitting(true);
      setErrorMsg("");
      setNotificationSent(false);

      await saveMessageContactForm({
        name,
        email,
        message,
      });

      try {
        await sendNotificationToAdmin(notificationMessage);
      } catch (notificationError) {
        console.error(
          "Message enregistré, mais notification admin non envoyée :",
          notificationError
        );
      }

      setNotificationSent(true);
      setFormData(INITIAL_FORM);
    } catch (error) {
      console.error("Erreur lors de l’envoi du message :", error);
      setErrorMsg(
        "Impossible d’envoyer votre message pour le moment. Merci de réessayer dans quelques instants."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative overflow-hidden bg-slate-50 py-20 text-slate-950 dark:bg-slate-950 dark:text-white lg:py-28"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.13),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.13)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.13)_1px,transparent_1px)] bg-[size:42px_42px] opacity-35 dark:opacity-15" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
            <FaPaperPlane />
            Nous contacter
          </div>

          <h2 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Contactez{" "}
            <span className="text-primary">
              nous
            </span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            Une question sur les cours, les horaires, les inscriptions ou la vie
            du club ? Envoyez-nous un message, nous vous répondrons rapidement.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal variant="left">
            <aside className="relative h-full overflow-hidden rounded-[2.5rem] bg-slate-950 p-6 text-white shadow-2xl sm:p-8">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.35),transparent_36%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.25),transparent_34%)]" />
              <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:42px_42px] opacity-20" />

              <div className="relative">
                <div className="flex h-16 w-16 items-center justify-center rounded-[1.4rem] bg-white text-2xl text-primary shadow-xl">
                  <FaLocationArrow />
                </div>

                <h3 className="mt-7 font-barlow text-3xl font-bold leading-tight sm:text-4xl">
                  Une information ?
                  <br />
                  Contactez {clubInfo.nom}
                </h3>

                <p className="mt-4 text-sm leading-7 text-white/70">
                  Retrouvez les coordonnées principales du club ou utilisez le
                  formulaire pour envoyer directement votre demande.
                </p>

                <div className="mt-8 space-y-4">
                  <ContactInfoItem
                    icon={FaEnvelope}
                    label="Email"
                    value={clubInfo.email}
                    href={emailHref}
                  />

                  <ContactInfoItem
                    icon={FaPhoneAlt}
                    label="Téléphone"
                    value={clubInfo.phone}
                    href={phoneHref}
                  />

                  <ContactInfoItem
                    icon={FaMapMarkerAlt}
                    label="Adresse"
                    value={clubInfo.address}
                  />
                </div>

                {(clubInfo.facebook || clubInfo.instagram) && (
                  <div className="mt-8 flex items-center gap-3">
                    {clubInfo.facebook && isExternalURL(clubInfo.facebook) && (
                      <a
                        href={clubInfo.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/10 text-white transition hover:-translate-y-0.5 hover:bg-white hover:text-blue-600"
                        aria-label="Facebook"
                      >
                        <FaFacebookF />
                      </a>
                    )}

                    {clubInfo.instagram && isExternalURL(clubInfo.instagram) && (
                      <a
                        href={clubInfo.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/10 text-white transition hover:-translate-y-0.5 hover:bg-white hover:text-pink-600"
                        aria-label="Instagram"
                      >
                        <FaInstagram />
                      </a>
                    )}
                  </div>
                )}
              </div>
            </aside>
          </Reveal>

          <Reveal variant="right" delay={120}>
            <form
              className="rounded-[2.5rem] border border-slate-200 bg-white/90 p-6 shadow-xl backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/80 sm:p-8"
              onSubmit={handleSubmit}
            >
              <div className="mb-8">
                <h3 className="font-barlow text-3xl font-bold text-slate-950 dark:text-white">
                  Envoyer un message
                </h3>
                <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                  Complétez le formulaire ci-dessous. Les champs marqués sont
                  nécessaires pour pouvoir vous répondre.
                </p>
              </div>

              <input
                type="text"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                className="hidden"
                tabIndex="-1"
                autoComplete="off"
              />

              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className={labelBase}>
                    Nom Prénom
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className={inputBase}
                    placeholder="Votre nom et prénom"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    disabled={submitting}
                  />
                </div>

                <div>
                  <label htmlFor="email" className={labelBase}>
                    Votre email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className={inputBase}
                    placeholder="mail@gmail.com"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="message" className={labelBase}>
                  Votre message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows="7"
                  className={`${inputBase} resize-none`}
                  placeholder="Bonjour, je souhaite obtenir des informations sur..."
                  required
                  value={formData.message}
                  onChange={handleInputChange}
                  disabled={submitting}
                />
              </div>

              {errorMsg && (
                <div className="mt-5 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
                  <FaExclamationTriangle className="mt-0.5 shrink-0" />
                  <span>{errorMsg}</span>
                </div>
              )}

              {notificationSent && (
                <div className="mt-5 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-semibold text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
                  <FaCheckCircle className="mt-0.5 shrink-0" />
                  <span>
                    Votre message a bien été envoyé. Merci, nous reviendrons
                    vers vous rapidement.
                  </span>
                </div>
              )}

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs leading-5 text-slate-400">
                  Votre message sera transmis à l’équipe du club.
                </p>

                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-3 rounded-full bg-slate-950 px-7 py-3.5 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-primary disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-slate-950 dark:hover:bg-primary dark:hover:text-white"
                >
                  {submitting ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane />
                      Envoyer le message
                    </>
                  )}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}